"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  Upload,
  Sparkles,
  FileText,
  Globe,
  CheckCircle2,
} from "lucide-react";
import type { WizardStep } from "@/hooks/use-scan-wizard";

interface WizardStepsProps {
  currentStep: WizardStep;
  stepIndex: number;
}

const steps: Array<{
  id: WizardStep;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  labelKey: string;
}> = [
  { id: "upload", icon: Upload, labelKey: "wizardUpload" },
  { id: "detect", icon: Sparkles, labelKey: "wizardDetect" },
  { id: "type", icon: FileText, labelKey: "wizardType" },
  { id: "language", icon: Globe, labelKey: "wizardLanguage" },
  { id: "confirm", icon: CheckCircle2, labelKey: "wizardConfirm" },
];

export function WizardSteps({ currentStep, stepIndex }: WizardStepsProps) {
  const t = useTranslations("scan");

  return (
    <div className="space-y-2">
      {/* Progress bar */}
      <div className="flex items-center gap-1.5">
        {steps.map((s, i) => (
          <div key={s.id} className="flex-1 relative">
            <div
              className={cn(
                "h-1 rounded-full transition-all duration-500 ease-out",
                i < stepIndex
                  ? "bg-brand"
                  : i === stepIndex
                    ? "bg-brand/70"
                    : "bg-accent"
              )}
            />
          </div>
        ))}
      </div>

      {/* Step labels */}
      <div className="flex justify-between">
        {steps.map((s, i) => (
          <div
            key={s.id}
            className={cn(
              "flex items-center gap-1 transition-colors duration-300",
              i <= stepIndex ? "text-foreground" : "text-muted-foreground/50"
            )}
          >
            <s.icon
              size={11}
              className={cn(
                "transition-colors duration-300",
                i < stepIndex
                  ? "text-brand"
                  : i === stepIndex
                    ? "text-brand"
                    : "text-muted-foreground/40"
              )}
            />
            <span className="text-[10px] font-medium hidden sm:inline">
              {t(s.labelKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
